import { Auth } from '@nackle/bridge';
import { DEFAULT_LOCALE_CODE } from '../../const';
import { ID_ENDPOINT } from '../../endpoints';
import { ACTIVE_THEME_NAME, THEME_CONFIG_HOYA, THEME_CONFIG_SEIKO } from '../../branding';
import { IS_INTERNAL_SITE } from '../../const/segmentation';

const SAML2_SETTINGS_AZURE = 'SAML2:3833e8f6-867e-4807-ae6a-07c8037b8b59';
const SAML2_SETTINGS_SALESFORCE = 'SAML2:6a315d50-d251-40da-98a7-75e1589d1121';
const AUTH_SOURCE_KEY_UNKNOWN = 'unknown';
const AUTH_SOURCE_KEY_ROSTER = 'roster';
const AUTH_SOURCE_KEY_SALESFORCE = 'salesforce';
const AUTH_SOURCE_KEY_AZURE = 'azure';
const AUTH_THEME_KEY_SEIKO_SALESFORCE = 'seiko';

/**
 * @param { { source: 'salesforce' | 'azure' | 'roster', theme: 'hoya' | 'seiko', locale: string } } config 
 */
export function buildAuth( {
    source = AUTH_SOURCE_KEY_SALESFORCE,
    theme = THEME_CONFIG_HOYA.name, 
    locale = DEFAULT_LOCALE_CODE
} = {} ) {
    
    const authSettings = {
        authority: ID_ENDPOINT,
        scope: 'openid profile company permissions email recovery address',
        extraQueryParams: {},
        debug: false
    };

    // Adjust the SAML2 settings to change the authentication source.
    switch ( source ) {
        case AUTH_SOURCE_KEY_ROSTER:
            authSettings.extraQueryParams.directAccess = true;
            break;

        case AUTH_SOURCE_KEY_AZURE:
            authSettings.extraQueryParams.settings = SAML2_SETTINGS_AZURE;
            break;

        case AUTH_SOURCE_KEY_SALESFORCE:
        default:
            authSettings.extraQueryParams.settings = SAML2_SETTINGS_SALESFORCE;
            break;
    }

    // Adjust the SAML2 settings to set the theme the login page.
    switch ( theme ) {
        case THEME_CONFIG_SEIKO.name:
            // The `relayState` param is used for SalesForce. Currently there is no
            // parameter known for theming Azure.
            authSettings.extraQueryParams.relayState = AUTH_THEME_KEY_SEIKO_SALESFORCE;
            break;

        case THEME_CONFIG_HOYA.name:
        default:
            // No options needed at the moment to theme the login pages for HOYA.
            break;
    }

    // Set the locale if it exists
    if ( locale ) {
        // TODO: Bridge/ID doesn't actually support this yet, so this might need to be updated once it can.
        // authSettings.extraQueryParams.locale = locale;
    }

    if ( new URLSearchParams( window.location.search ).get( 'method' ) === 'direct' ) {
        authSettings.extraQueryParams.directAccess = true;
        delete authSettings.extraQueryParams.settings;
        delete authSettings.extraQueryParams.relayState;
        console.log( authSettings );
    }

    return new Auth( authSettings );
}

/**
 * Returns the type of the auth object.
 * 
 * @param { object } auth 
 * @returns { 'roster' | 'salesforce' | 'azure' | 'unknown' }
 */
export function getAuthType( auth ) {
    const roster = ( auth?.options?.extraQueryParams?.directAccess === true );
    if ( roster ) {
        return AUTH_SOURCE_KEY_ROSTER;
    }

    const azure = ( auth?.options?.extraQueryParams?.settings === SAML2_SETTINGS_AZURE );
    if ( azure ) {
        return AUTH_SOURCE_KEY_AZURE;
    }

    const salesforce = ( auth?.options?.extraQueryParams?.settings === SAML2_SETTINGS_SALESFORCE );
    if ( salesforce ) {
        return AUTH_SOURCE_KEY_SALESFORCE;
    }

    return AUTH_SOURCE_KEY_UNKNOWN;
}

// Determine the default authentication method depending on the domain. Internal
// users should be brought to the Azure login page and non-internal users should
// be brought to the SalesForce login page.
export let auth = buildAuth( {
    source: ( IS_INTERNAL_SITE ) ? AUTH_SOURCE_KEY_AZURE : AUTH_SOURCE_KEY_SALESFORCE,
    theme: ACTIVE_THEME_NAME
} );

export function setAuthConfig( config ) {
    auth = config;
}
